import { ModalAction } from '@overlayqa/shared/ui';
/**
 * The Figma files a project is linked to, on the project's Settings tab
 * (spec docs/superpowers/specs/2026-09-10-project-figma-files-design.md).
 *
 * One list, default first. The default is the file both frame pickers open on
 * and the one the single-slot `figmaFileKey` mirrors; every other file is one
 * pick away in those pickers. Each row names who linked it, which the single
 * slot never did: on a team, a file replaced with no attribution read as a
 * teammate's setup silently breaking.
 *
 * Deliberately says nothing about the model. Jira, Linear and Zeplin all let a
 * project hold many designs and none of them explains it in the UI.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { PlusSignIcon } from '@hugeicons/core-free-icons';
import { linkedFrameNodeId } from '@overlayqa/shared';
import type { ParsedFigmaUrl, ProjectFigmaFile } from '@overlayqa/shared';
import { FigmaUrlInput } from '../figma/FigmaUrlInput';
import {
  useAddProjectFigmaFile,
  useRemoveProjectFigmaFile,
  useSetDefaultProjectFigmaFile,
} from '../../hooks/useProjects';
import { timeAgo } from '../../lib/utils';
import { log } from '@/lib/logger';

interface ProjectFigmaFilesSectionProps {
  projectId: string;
  files: ProjectFigmaFile[];
  /** Mirrors requireProjectWrite: viewers see the list and no controls. */
  canWrite: boolean;
  /**
   * A valid Figma link typed or pasted but not linked yet, or null. The Project
   * Settings modal links it on Save, so the paste is not silently dropped when
   * the user reaches for the modal's primary button instead of Link.
   */
  onPendingChange?: (parsed: ParsedFigmaUrl | null) => void;
}

function linkedByLine(file: ProjectFigmaFile): string {
  const when = timeAgo(file.createdAt);
  return file.linkedByName ? `Linked by ${file.linkedByName}, ${when}` : `Linked ${when}`;
}

export function ProjectFigmaFilesSection({ projectId, files, canWrite, onPendingChange }: ProjectFigmaFilesSectionProps) {
  const addFile = useAddProjectFigmaFile();
  const setDefault = useSetDefaultProjectFigmaFile();
  const removeFile = useRemoveProjectFigmaFile();
  // Per-row busy flags so one row's label never reflects another row's request.
  const [busyId, setBusyId] = useState<string | null>(null);
  // FigmaUrlInput keeps its text after a successful save; remounting it is how
  // the field clears for the next file.
  const [inputEpoch, setInputEpoch] = useState(0);
  // With files linked, the paste field sits behind "Add Figma file"
  // (Emily, 2026-09-17: "should be a button cta which shows url when selected").
  const [adding, setAdding] = useState(false);

  const hasDefault = files.some((f) => f.isDefault);

  const handleAdd = async (parsed: ParsedFigmaUrl) => {
    log.info('[ProjectFigmaFiles] adding file', { projectId, fileKey: parsed.fileKey });
    await addFile.mutateAsync({
      projectId,
      data: { fileKey: parsed.fileKey, nodeId: linkedFrameNodeId(parsed.nodeId), name: parsed.fileName ?? null },
    });
    setInputEpoch((n) => n + 1);
    setAdding(false);
    onPendingChange?.(null);
  };

  const cancelAdd = () => {
    setAdding(false);
    setInputEpoch((n) => n + 1);
    onPendingChange?.(null);
  };

  const handleSetDefault = async (file: ProjectFigmaFile) => {
    setBusyId(file.id);
    try {
      await setDefault.mutateAsync({ projectId, fileId: file.id });
    } catch (err) {
      log.error('[ProjectFigmaFiles] set default failed', { projectId, fileId: file.id, error: err instanceof Error ? err.message : String(err) });
      toast.error(err instanceof Error && err.message ? err.message : 'Could not change the default file. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlink = async (file: ProjectFigmaFile) => {
    setBusyId(file.id);
    try {
      await removeFile.mutateAsync({ projectId, fileId: file.id });
    } catch (err) {
      log.error('[ProjectFigmaFiles] unlink failed', { projectId, fileId: file.id, error: err instanceof Error ? err.message : String(err) });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to unlink Figma file. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      {files.length === 0 ? (
        <p className="state-note">No Figma file linked to this project.</p>
      ) : (
        <>
          {!hasDefault && <p className="state-note">No default file yet.</p>}
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {files.map((file) => {
              const busy = busyId === file.id;
              return (
                <li key={file.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-700">{file.name ?? file.fileKey}</span>
                      {file.isDefault && (
                        <span className="rounded-[3px] bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700">Default</span>
                      )}
                    </div>
                    <p className="field-hint">{linkedByLine(file)}</p>
                  </div>
                  {canWrite && (
                    <div className="flex items-center gap-2">
                      {!file.isDefault && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={busy}
                          onClick={() => void handleSetDefault(file)}
                        ><ModalAction kind="done">
                          Set as default
                        </ModalAction></button>
                      )}
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => void handleUnlink(file)}
                      ><ModalAction kind="unlink">
                        {busy && removeFile.isPending ? 'Unlinking...' : 'Unlink'}
                      </ModalAction></button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {canWrite && files.length > 0 && !adding && (
        <div>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setAdding(true)}>
            <HugeiconsIcon icon={PlusSignIcon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="text-cap-trim">Add Figma file</span>
          </button>
        </div>
      )}

      {canWrite && (files.length === 0 || adding) && (
        <div>
          <FigmaUrlInput
            key={inputEpoch}
            context="project"
            showLabel={false}
            size="lg"
            saveButtonVariant="secondary"
            onChange={(_url, parsed) => onPendingChange?.(parsed)}
            onSave={handleAdd}
            saveButtonAccessory={
              adding ? (
                <button type="button" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" onClick={cancelAdd}><ModalAction kind="cancel">
                  Cancel
                </ModalAction></button>
              ) : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
